import { supabase } from '../lib/supabase'
import type { UploadedInvoice, UploadInvoiceData, UploadInvoiceResult } from '../types/invoice'

class UploadedInvoiceService {
  private readonly PLAN_LIMITS = {
    freemium: { maxUploadedInvoices: 5, hasUnlimitedUploads: false },
    professional: { maxUploadedInvoices: -1, hasUnlimitedUploads: true },
    studio: { maxUploadedInvoices: -1, hasUnlimitedUploads: true }
  }

  private checkSupabase() {
    if (!supabase) {
      throw new Error('Supabase client not initialized')
    }
    return supabase
  }

  // Create storage bucket if it doesn't exist
  async createStorageBucketIfNeeded(): Promise<{ success: boolean; error?: string }> {
    try {
      // First check if bucket exists
      const { data: buckets, error: listError } = await this.checkSupabase().storage.listBuckets()
      
      if (listError) {
        console.error('Error listing buckets:', listError)
        return { success: false, error: listError.message }
      }

      const bucketExists = buckets?.some(bucket => bucket.id === 'uploaded-invoices')
      
      if (bucketExists) {
        console.log('Storage bucket already exists')
        return { success: true }
      }

      // Create the bucket
      const { data, error } = await this.checkSupabase().storage.createBucket('uploaded-invoices', {
        public: false,
        fileSizeLimit: 52428800, // 50MB
        allowedMimeTypes: [
          'application/pdf',
          'image/jpeg', 
          'image/jpg',
          'image/png',
          'image/gif',
          'image/webp'
        ]
      })

      if (error) {
        console.error('Error creating bucket:', error)
        return { success: false, error: error.message }
      }

      console.log('Storage bucket created successfully:', data)
      return { success: true }
    } catch (error) {
      console.error('Unexpected error creating bucket:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create storage bucket'
      }
    }
  }

  // Get upload limits for current user
  async getUploadLimits(userId: string, subscriptionStatus: string): Promise<{
    maxUploadedInvoices: number
    hasUnlimitedUploads: boolean
    currentUploadedCount: number
    canUploadInvoice: boolean
  }> {
    try {
      // Get current upload count
      const { count, error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)

      if (error) {
        throw error
      }

      const currentUploadedCount = count || 0
      const planType = subscriptionStatus as keyof typeof this.PLAN_LIMITS
      const limits = this.PLAN_LIMITS[planType] || this.PLAN_LIMITS.freemium

      return {
        maxUploadedInvoices: limits.maxUploadedInvoices,
        hasUnlimitedUploads: limits.hasUnlimitedUploads,
        currentUploadedCount,
        canUploadInvoice: limits.hasUnlimitedUploads || currentUploadedCount < limits.maxUploadedInvoices
      }
    } catch (error) {
      console.error('Error getting upload limits:', error)
      // Return permissive defaults on error
      const planType = subscriptionStatus as keyof typeof this.PLAN_LIMITS
      const limits = this.PLAN_LIMITS[planType] || this.PLAN_LIMITS.freemium
      
      return {
        maxUploadedInvoices: limits.maxUploadedInvoices,
        hasUnlimitedUploads: limits.hasUnlimitedUploads,
        currentUploadedCount: 0,
        canUploadInvoice: true
      }
    }
  }

  // Upload an invoice file
  async uploadInvoice(userId: string, data: UploadInvoiceData): Promise<UploadInvoiceResult> {
    try {
      const { file, title, clientName, amount, issueDate, dueDate, notes } = data

      // Validate file
      if (!file) {
        return { success: false, error: 'No file provided' }
      }

      // Check file size (50MB limit)
      if (file.size > 52428800) {
        return { success: false, error: 'File size must be less than 50MB' }
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        return { success: false, error: 'File type not supported. Please upload PDF or image files.' }
      }

      // Debug: Check current authentication state
      console.log('Upload attempt - User ID:', userId)
      const { data: { session }, error: sessionError } = await this.checkSupabase().auth.getSession()
      console.log('Current session:', { 
        userId: session?.user?.id, 
        email: session?.user?.email,
        sessionError: sessionError?.message 
      })

      // Verify user is authenticated and matches the provided userId
      if (!session?.user) {
        return { success: false, error: 'User not authenticated. Please log in and try again.' }
      }

      if (session.user.id !== userId) {
        console.error('User ID mismatch:', { sessionUserId: session.user.id, providedUserId: userId })
        return { success: false, error: 'Authentication error. Please refresh the page and try again.' }
      }

      // Ensure storage bucket exists before uploading
      const bucketResult = await this.createStorageBucketIfNeeded()
      if (!bucketResult.success) {
        console.warn('Could not create storage bucket:', bucketResult.error)
        // Continue anyway - bucket might exist but we can't detect it
      }

      // Create unique filename
      const timestamp = Date.now()
      const fileName = `${userId}/${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`

      // Upload file to Supabase Storage
      const { error: uploadError } = await this.checkSupabase().storage
        .from('uploaded-invoices')
        .upload(fileName, file)

      if (uploadError) {
        console.error('File upload error:', uploadError)
        
        // If bucket not found, try to create it and retry once
        if (uploadError.message.includes('Bucket not found')) {
          console.log('Bucket not found, attempting to create and retry...')
          const retryBucketResult = await this.createStorageBucketIfNeeded()
          
          if (retryBucketResult.success) {
            // Retry the upload
            const { error: retryUploadError } = await this.checkSupabase().storage
              .from('uploaded-invoices')
              .upload(fileName, file)
              
            if (retryUploadError) {
              return { success: false, error: `Upload failed after retry: ${retryUploadError.message}` }
            }
          } else {
            return { success: false, error: `Bucket creation failed: ${retryBucketResult.error}` }
          }
        } else {
          return { success: false, error: `Upload failed: ${uploadError.message}` }
        }
      }

      // Get public URL
      const { data: urlData } = this.checkSupabase().storage
        .from('uploaded-invoices')
        .getPublicUrl(fileName)

      // Debug: Log the data we're about to insert
      const insertData = {
        user_id: userId,
        file_name: fileName,
        original_file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        file_url: urlData.publicUrl,
        title,
        client_name: clientName,
        amount,
        issue_date: issueDate,
        due_date: dueDate,
        notes
      }
      console.log('Attempting to insert invoice data:', insertData)

      // Save invoice record to database
      const { data: invoiceData, error: dbError } = await this.checkSupabase()
        .from('uploaded_invoices')
        .insert(insertData)
        .select()
        .single()

      if (dbError) {
        // Clean up uploaded file if database insert fails
        await this.checkSupabase().storage
          .from('uploaded-invoices')
          .remove([fileName])
        
        console.error('Database insert error:', dbError)
        console.error('Error details:', {
          code: dbError.code,
          message: dbError.message,
          details: dbError.details,
          hint: dbError.hint
        })

        // Provide more specific error messages based on the error type
        if (dbError.message.includes('row-level security policy')) {
          return { 
            success: false, 
            error: 'Permission denied. Please refresh the page and try again. If the problem persists, contact support.' 
          }
        } else if (dbError.message.includes('violates foreign key constraint')) {
          return { 
            success: false, 
            error: 'User authentication error. Please log out and log back in.' 
          }
        } else {
          return { success: false, error: `Failed to save invoice: ${dbError.message}` }
        }
      }

      console.log('Invoice uploaded successfully:', invoiceData)
      return {
        success: true,
        uploadedInvoice: this.transformUploadedInvoiceFromDB(invoiceData)
      }
    } catch (error) {
      console.error('Error uploading invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload invoice'
      }
    }
  }

  // Get all uploaded invoices for a user
  async getUploadedInvoices(userId: string): Promise<{
    success: boolean
    invoices?: UploadedInvoice[]
    error?: string
  }> {
    try {
      const { data, error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .select('*')
        .eq('user_id', userId)
        .order('uploaded_at', { ascending: false })

      if (error) {
        throw error
      }

      return {
        success: true,
        invoices: data?.map(this.transformUploadedInvoiceFromDB) || []
      }
    } catch (error) {
      console.error('Error fetching uploaded invoices:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch uploaded invoices'
      }
    }
  }

  // Update uploaded invoice metadata
  async updateUploadedInvoice(
    invoiceId: string,
    updates: Partial<Pick<UploadedInvoice, 'title' | 'clientName' | 'amount' | 'issueDate' | 'dueDate' | 'notes'>>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .update({
          title: updates.title,
          client_name: updates.clientName,
          amount: updates.amount,
          issue_date: updates.issueDate,
          due_date: updates.dueDate,
          notes: updates.notes
        })
        .eq('id', invoiceId)

      if (error) {
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error updating uploaded invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update invoice'
      }
    }
  }

  // Delete uploaded invoice
  async deleteUploadedInvoice(invoiceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // First get the invoice to get the file path
      const { data: invoice, error: fetchError } = await this.checkSupabase()
        .from('uploaded_invoices')
        .select('file_name')
        .eq('id', invoiceId)
        .single()

      if (fetchError) {
        throw fetchError
      }

      // Delete the file from storage
      const { error: storageError } = await this.checkSupabase().storage
        .from('uploaded-invoices')
        .remove([invoice.file_name])

      if (storageError) {
        console.error('Storage deletion error:', storageError)
        // Continue with database deletion even if storage deletion fails
      }

      // Delete the database record
      const { error: dbError } = await this.checkSupabase()
        .from('uploaded_invoices')
        .delete()
        .eq('id', invoiceId)

      if (dbError) {
        throw dbError
      }

      return { success: true }
    } catch (error) {
      console.error('Error deleting uploaded invoice:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete invoice'
      }
    }
  }

  // Transform database record to frontend format
  private transformUploadedInvoiceFromDB(dbInvoice: any): UploadedInvoice {
    return {
      id: dbInvoice.id,
      userId: dbInvoice.user_id,
      fileName: dbInvoice.file_name,
      originalFileName: dbInvoice.original_file_name,
      fileSize: dbInvoice.file_size,
      fileType: dbInvoice.file_type,
      fileUrl: dbInvoice.file_url,
      title: dbInvoice.title,
      clientName: dbInvoice.client_name,
      amount: dbInvoice.amount ? parseFloat(dbInvoice.amount.toString()) : undefined,
      issueDate: dbInvoice.issue_date,
      dueDate: dbInvoice.due_date,
      notes: dbInvoice.notes,
      status: dbInvoice.status || 'draft',
      paymentStatus: dbInvoice.payment_status || 'unpaid',
      paidAmount: dbInvoice.paid_amount ? parseFloat(dbInvoice.paid_amount.toString()) : 0,
      paidDate: dbInvoice.paid_date,
      paymentMethod: dbInvoice.payment_method,
      paymentNotes: dbInvoice.payment_notes,
      uploadedAt: dbInvoice.uploaded_at,
      updatedAt: dbInvoice.updated_at
    }
  }

  // Update invoice status
  async updateInvoiceStatus(invoiceId: string, status: UploadedInvoice['status']): Promise<{
    success: boolean
    error?: string
  }> {
    try {
      const { error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId)

      if (error) {
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error updating invoice status:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update invoice status'
      }
    }
  }

  // Mark invoice as paid
  async markAsPaid(invoiceId: string, paidAmount?: number, paymentMethod?: string, paymentNotes?: string): Promise<{
    success: boolean
    error?: string
  }> {
    try {
      const updates: any = {
        status: 'paid',
        payment_status: 'paid',
        paid_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
        updated_at: new Date().toISOString()
      }

      if (paidAmount !== undefined) {
        updates.paid_amount = paidAmount
      }
      if (paymentMethod) {
        updates.payment_method = paymentMethod
      }
      if (paymentNotes) {
        updates.payment_notes = paymentNotes
      }

      const { error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .update(updates)
        .eq('id', invoiceId)

      if (error) {
        throw error
      }

      // Dispatch custom event to notify other components about the payment
      const event = new CustomEvent('invoiceStatusChanged', {
        detail: {
          invoiceId,
          newStatus: 'paid',
          amount: paidAmount,
          isUploadedInvoice: true
        }
      })
      window.dispatchEvent(event)

      return { success: true }
    } catch (error) {
      console.error('Error marking invoice as paid:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to mark invoice as paid'
      }
    }
  }

  // Update payment information
  async updatePaymentInfo(invoiceId: string, paymentData: {
    paymentStatus?: UploadedInvoice['paymentStatus']
    paidAmount?: number
    paidDate?: string
    paymentMethod?: string
    paymentNotes?: string
  }): Promise<{
    success: boolean
    error?: string
  }> {
    try {
      const updates: any = {
        updated_at: new Date().toISOString()
      }

      if (paymentData.paymentStatus) {
        updates.payment_status = paymentData.paymentStatus
        // Auto-update status based on payment status
        if (paymentData.paymentStatus === 'paid') {
          updates.status = 'paid'
        }
      }
      if (paymentData.paidAmount !== undefined) {
        updates.paid_amount = paymentData.paidAmount
      }
      if (paymentData.paidDate) {
        updates.paid_date = paymentData.paidDate
      }
      if (paymentData.paymentMethod) {
        updates.payment_method = paymentData.paymentMethod
      }
      if (paymentData.paymentNotes) {
        updates.payment_notes = paymentData.paymentNotes
      }

      const { error } = await this.checkSupabase()
        .from('uploaded_invoices')
        .update(updates)
        .eq('id', invoiceId)

      if (error) {
        throw error
      }

      // Dispatch custom event if payment status changed to paid
      if (paymentData.paymentStatus === 'paid') {
        const event = new CustomEvent('invoiceStatusChanged', {
          detail: {
            invoiceId,
            newStatus: 'paid',
            amount: paymentData.paidAmount,
            isUploadedInvoice: true
          }
        })
        window.dispatchEvent(event)
      }

      return { success: true }
    } catch (error) {
      console.error('Error updating payment info:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update payment information'
      }
    }
  }
}

export const uploadedInvoiceService = new UploadedInvoiceService() 